import React, { useEffect, useState, ChangeEvent } from "react";
import { useHistory } from "react-router-dom";
import { useParams } from "react-router";
import useModal from "hooks/modals/useModal";

import Page from "components/page/Page";
import Card from "components/card/Card";
import Input from "components/form/input/Input";
import Toggle from "components/form/toggle/Toggle";
import { Select } from "components/select/index";
import Popup from "components/popup/Popup";

import { IAuthState } from "redux/reducers/auth";
import { IUIActions, showError, showSuccess } from "redux/actions/ui";

import { useTranslation } from "react-i18next";
import { StatusCode } from "api/protocols";
import FormLayout from "components/form/FormLayout";
import { cloneDeep, isEqualWith } from "lodash";
import { DeleteDiscount, GetDiscount, PutDiscount } from "api/rpc/2024-04/facilityAdmin/discount/discount";
import DiscountProductSelect from "./DiscountProductSelect/DiscountProductSelect";
import { IProduct } from "redux/reducers/models/product";
import { useAppDispatch } from "hooks/redux";
import { IDepartments } from "api/rpc/2024-04/facility";
import { TCustomerType } from "redux/reducers/models/facility";
import { IMultipleActionDropdownPageAction } from "components/page/PageActions";
import Sheet from "components/sheet/Sheet";
import DiscountDateSelect, { ITimeState } from "./DiscountDateSelect/DiscountDateSelect";
import { formatDate } from "helpers/Helpers";
import DiscountDepartmentSelect from "./DiscountDepartmentSelect/DiscountDepartmentSelect";
import DiscountCustomerTypeSelect from "./DiscountCustomerTypeSelect/DiscountCustomerTypeSelect";

interface IEditDiscountProps {
  authStore: IAuthState;
  uiActions: IUIActions;
}

export interface IDiscountState {
  title: string;
  application: any;
  value_type: any;
  value: number;
  custom: boolean;
  tracked: boolean;
  departments?: IDepartments[];
  products?: IProduct[];
  discounted_customer_types?: TCustomerType[];
}

export default function EditDiscount(props: IEditDiscountProps) {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();

  const today = new Date();
  const [state, setState] = useState<IDiscountState>({
    title: "",
    application: null,
    value_type: null,
    value: 0,
    custom: false,
    tracked: false,
    departments: [],
    products: [],
    discounted_customer_types: [],
  });

  const [timeState, setTimeState] = useState<ITimeState>({
    start_date: today,
    end_date: today,
    daily_start_time: "",
    daily_end_time: "",
    interval: "weeks",
    auto_apply: false,
    ends_type: "never",
    schedule_type: "date_range",
    repeat_amount: 1,
  });

  const [discountBeforeChanges, setDiscountBeforeChanges] = useState<IDiscountState>(undefined);
  const [timeBeforeChanges, setTimeBeforeChanges] = useState<ITimeState>(undefined);

  const [discountLoaded, setDiscountLoaded] = useState<boolean>(false);

  const [discountModalVisibility, setDiscountModalVisibility] = useState(false);
  const [departmentModalVisibility, setDepartmentModalVisibility] = useState(false);
  const [customerTypeModalVisibility, setCustomerTypeModalVisibility] = useState(false);
  const [applyDiscountRangesToggle, setApplyDiscountRangesToggle] = useState(false);
  const [applyDiscountRangesToggleBefore, setApplyDiscountRangesToggleBefore] = useState(false);

  const [applyDiscountTimesToggle, setApplyDiscountTimesToggle] = useState(false);
  const [applyDiscountTimesToggleBefore, setApplyDiscountTimesToggleBefore] = useState(false);
  const permissions = props.authStore?.user.permissions;
  const { Option } = Select;

  const setStateCallback = <T,>(id: string, data: T) => {
    setState(prev => ({ ...prev, [id]: data }));
  };

  const setApplication = <T extends Record<string, any>>(updated: T, id: string) => {
    setState(prev => ({ ...prev, [id]: updated }));
  };

  const {
    state: deleteDiscountPopup,
    updateModal: updateDeleteDiscountPopup,
    closeModal: closeDeleteDiscountPopup,
  } = useModal();

  useEffect(() => {
    void loadDiscount();
  }, []);

  function handleInputChange(event: any) {
    const { id, value } = event.target;
    setState({ ...state, [id]: value });
  }

  function handleDropdownChange(value: string) {
    setState({ ...state, value_type: value });
  }

  function handleToggleChange(event: ChangeEvent<HTMLInputElement>) {
    const id = event?.target?.id;
    const checked = event?.target?.checked;

    if (id === undefined || checked === undefined) {
      return;
    }

    if (id === "auto_apply") {
      setTimeState(prev => ({ ...prev, auto_apply: checked }));
    } else {
      setState({ ...state, [id]: checked });
    }
  }

  async function loadDiscount() {
    const discountRes = await GetDiscount(
      {
        id: Number(id),
        products: true,
        departments: true,
        customer_types: true,
      },
      true,
    );

    if (discountRes.status !== StatusCode.OK) {
      return;
    }

    setState({
      ...state,
      title: discountRes.data[0].title,
      application: discountRes.data[0].application,
      value_type: discountRes.data[0].value_type,
      value: discountRes.data[0].value,
      custom: discountRes.data[0].custom,
      tracked: discountRes.data[0].tracked === undefined ? false : discountRes.data[0].tracked, //Tracked value is currently not stores in the back end
      products: discountRes.data[0].products ?? [],
      departments: discountRes.data[0].departments ?? [],
      discounted_customer_types: discountRes.data[0].customer_types ?? [],
    });

    const startDate = discountRes.data[0].start_date ? String(discountRes.data[0].start_date) + "T00:00:00" : today;
    const endDate = discountRes.data[0].end_date ? String(discountRes.data[0].end_date) + "T00:00:00" : today;

    setTimeState({
      auto_apply: discountRes.data[0].auto_apply,
      start_date: new Date(startDate),
      end_date: new Date(endDate),
      daily_start_time: discountRes.data[0].daily_start_time,
      daily_end_time: discountRes.data[0].daily_end_time,
      interval: discountRes.data[0].repeat_interval ?? "weeks",
      repeat_amount: discountRes.data[0].repeat_amount ?? 1,
      weekdays: discountRes.data[0].week_days,
      ends_type: discountRes.data[0].end_date ? "on" : "never",
      schedule_type: discountRes.data[0].schedule_type,
    });

    setApplyDiscountRangesToggle(!(discountRes.data[0].end_date === null && discountRes.data[0].start_date === null));
    setApplyDiscountTimesToggle(
      !(discountRes.data[0].daily_end_time === null && discountRes.data[0].daily_start_time === null),
    );
    setDiscountLoaded(true);
  }

  async function updateDiscount() {
    const startDateBuffer = applyDiscountRangesToggle ? formatDate(timeState?.start_date) : null;
    const endDateBuffer = timeState?.ends_type === "on" ? formatDate(timeState?.end_date) : null;

    if (timeState.schedule_type === "nth_weekday" && timeState?.weekdays.length === 0) {
      dispatch(showError(t("You must select at least 1 day of the week to apply this discount to.")));
      return;
    }

    const discountRes = await PutDiscount(
      {
        id: Number(id),
        title: state.title,
        value_type: state.value_type,
        application: "line_item",
        value: state.value,
        custom: state.custom,
        tracked: state.tracked,
        auto_apply: timeState?.auto_apply ?? false,
        start_date: startDateBuffer,
        end_date: endDateBuffer,
        daily_start_time: timeState?.daily_start_time ?? null,
        daily_end_time: timeState?.daily_end_time ?? null,
        // Enhancements
        repeat_amount: Number(timeState?.repeat_amount),
        repeat_interval: timeState?.interval,
        week_days: timeState?.weekdays,
        schedule_type: timeState?.schedule_type,

        product_ids: state?.products.map(product => {
          return {
            product_id: product.id,
            application: product.pivot?.application,
          };
        }),
        department_ids: state?.departments.map(dept => {
          return {
            department_id: dept.id,
            application: dept.pivot.application,
          };
        }),
        customer_type_ids: state?.discounted_customer_types?.map(type => {
          return type.id;
        }),
      },
      true,
    );

    if (discountRes.status !== StatusCode.OK) {
      dispatch(showError(t("Can't Update Discount")));
      return;
    }

    dispatch(showSuccess(t("Discount Updated Successfully")));
    history.push("/admin/settings/discount");
  }

  async function handleDeleteDiscount() {
    const discountRes = await DeleteDiscount(
      {
        id: Number(id),
      },
      true,
    );

    if (discountRes.status !== StatusCode.OK) {
      props.uiActions.showError(t("Can't Delete Discount"));
      return;
    }

    dispatch(showSuccess(t("Discount Deleted Successfully")));
    history.push("/admin/settings/discount");
  }

  function handleDiscountProductModal() {
    setDiscountModalVisibility(!discountModalVisibility);
  }

  function handleDepartmentModal() {
    setDepartmentModalVisibility(!departmentModalVisibility);
  }

  function handleCustomerTypeModal() {
    setCustomerTypeModalVisibility(!customerTypeModalVisibility);
  }

  function handleDiscountDatesToggle() {
    setApplyDiscountRangesToggle(!applyDiscountRangesToggle);
  }

  function handleClear(id: string) {
    setState(prev => ({ ...prev, [id]: [] }));
  }

  function unsavedChangesExist() {
    if (discountBeforeChanges === undefined) {
      if (discountLoaded) {
        setDiscountBeforeChanges(cloneDeep(state));
        setTimeBeforeChanges(cloneDeep(timeState));
        setApplyDiscountRangesToggleBefore(applyDiscountRangesToggle);
        setApplyDiscountTimesToggleBefore(applyDiscountTimesToggle);
      }
      return false;
    }

    return !isEqualWith(discountBeforeChanges, state) || !isEqualWith(timeBeforeChanges, timeState);
  }

  function cancelUnsavedChanges() {
    setState(cloneDeep(discountBeforeChanges));
    setTimeState(cloneDeep(timeBeforeChanges));
    setApplyDiscountRangesToggle(applyDiscountRangesToggleBefore);
    setApplyDiscountTimesToggle(applyDiscountTimesToggleBefore);
    if (discountModalVisibility) {
      setDiscountModalVisibility(false);
    }
  }

  function updateTimeState(key: string, value: any) {
    setTimeState(prev => ({ ...prev, [key]: value }));
  }

  const secondaryActions: IMultipleActionDropdownPageAction = {
    label: t("Options"),
    dropdownProps: {
      alignment: "right",
      options: [
        {
          type: "handler",
          label: t("Delete"),
          handler: () => updateDeleteDiscountPopup({ isOpen: true }),
          icon: "trash-can",
          disabled: !permissions.discounts_create,
        },
        {
          type: "handler",
          label: t("Discount Product"),
          handler: handleDiscountProductModal,
          icon: "percentage",
        },
        {
          type: "handler",
          label: t("Discount Departments"),
          handler: handleDepartmentModal,
          icon: "tag",
        },
        {
          type: "handler",
          label: t("Discount Customer Types"),
          handler: handleCustomerTypeModal,
          icon: "person",
        },
      ],
    },
  };

  return (
    <Page
      title={t("Edit Discount")}
      narrow
      multipleActionDropdownAction={secondaryActions}
      breadcrumbs={[
        {
          prefix: true,
          label: t("Back to Discounts"),
          url: "/admin/settings/discount",
        },
      ]}
      notificationBarProps={{
        isVisible: unsavedChangesExist(),
        onAction: updateDiscount,
        onCancel: cancelUnsavedChanges,
      }}
    >
      <Card>
        <Card.Section>
          <FormLayout>
            <FormLayout.Group>
              <Input
                value={state.title}
                label={t("Title")}
                id="title"
                onChange={handleInputChange}
                placeholder={t("Title")}
                disabled={!permissions.discounts_create}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <Select
                defaultValue={state.value_type as string}
                label={t("Value Type")}
                className="flex justify-center align-center w-full h-10 position-relative z-20 text-black text-medium appearance-none border-none focus:outline-none placeholder-gray-200"
                placeholder={t("Value Type")}
                onChange={(value: string) => handleDropdownChange(value)}
                disabled={!permissions.discounts_create}
              >
                <Option value={"percent"}>{t("Percent")}</Option>
                <Option value={"fixed_amount"}>{t("Fixed Amount")}</Option>
                <Option value={"cost_plus_percent"}>{t("Cost + Percent")}</Option>
              </Select>
              <Input
                value={state.value}
                label={t("Value")}
                id="value"
                onChange={handleInputChange}
                placeholder={t("Value")}
                type="number"
                prefix={state.value_type === "percent" || state.value_type === "cost_plus_percent" ? "%" : "$"}
                disabled={!permissions.discounts_create}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <Toggle
                labelRight={t("Customizable")}
                id="custom"
                onChange={handleToggleChange}
                checked={state.custom}
                size="medium"
                disabled={!permissions.discounts_create}
              />

              <Toggle
                id="auto_apply"
                labelRight={t("Automatic Discount")}
                onChange={handleToggleChange}
                checked={timeState?.auto_apply}
                size="medium"
              />

              <Toggle
                id="apply_applicable_dates"
                labelRight={t("Applicable Dates")}
                onChange={handleDiscountDatesToggle}
                checked={applyDiscountRangesToggle}
                size="medium"
              />
            </FormLayout.Group>
          </FormLayout>
        </Card.Section>
      </Card>

      {applyDiscountRangesToggle ? (
        <DiscountDateSelect
          updateState={updateTimeState}
          timeState={timeState}
          show_dates={applyDiscountRangesToggle}
        />
      ) : null}

      <Popup
        open={deleteDiscountPopup.isOpen}
        title={t("Delete Discount?")}
        description={t("Are you sure you want to delete this discount?")}
        closable
        onOk={handleDeleteDiscount}
        onCancel={closeDeleteDiscountPopup}
        okText={t("Delete")}
        cancelText={t("Cancel")}
        type="warning"
        backDropCancel={false}
      />

      <Sheet
        open={discountModalVisibility}
        title={t("Discount Products")}
        okText={t("Apply")}
        onOk={handleDiscountProductModal}
        cancelText={t("Close")}
        okDisabled={state?.products?.some(
          product => product.pivot?.application === undefined || product.pivot?.application === null,
        )}
        onCancel={handleDiscountProductModal}
        secondaryText={t("Clear")}
        onSecondary={() => handleClear("products")}
        secondaryDisabled={state?.products.length === 0}
        backDropCancel={false}
        size="medium"
        closable
      >
        <DiscountProductSelect
          stateCallback={setStateCallback}
          onApplicationSelect={setApplication}
          products={state.products}
        />
      </Sheet>

      <Sheet
        open={departmentModalVisibility}
        title={t("Discount Departments")}
        okText={t("Apply")}
        onOk={handleDepartmentModal}
        okDisabled={state?.departments?.some(department => department.pivot?.application === undefined)}
        cancelText={t("Close")}
        onCancel={handleDepartmentModal}
        secondaryText={t("Clear")}
        onSecondary={() => handleClear("departments")}
        secondaryDisabled={state?.departments.length === 0}
        backDropCancel={false}
        size="medium"
        closable
      >
        <DiscountDepartmentSelect
          stateCallback={setStateCallback}
          onApplicationSelect={setApplication}
          discountDepartmentState={state?.departments}
        />
      </Sheet>

      <Sheet
        open={customerTypeModalVisibility}
        title={t("Discount Customer Types")}
        okText={t("Apply")}
        onOk={handleCustomerTypeModal}
        cancelText={t("Close")}
        onCancel={handleCustomerTypeModal}
        secondaryText={t("Clear")}
        onSecondary={() => handleClear("discounted_customer_types")}
        secondaryDisabled={state?.discounted_customer_types.length === 0}
        backDropCancel={false}
        size="small"
        closable
        overflow
      >
        <DiscountCustomerTypeSelect
          stateCallback={setStateCallback}
          discountCustomerType={state?.discounted_customer_types}
        />
      </Sheet>
    </Page>
  );
}
